<template>
  <!-- We define the keydown handler on the div to catch all events -->
  <!-- emitted from child elements. We do not care about the div. -->
  <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
  <div
    ref="modalElement"
    v-click-outside="handleClose"
    aria-modal="true"
    class="bg-surface-default-low shadow-long rounded-lg hidden max-h-[60vh] w-[320px] max-w-full flex-col pb-16 lg:flex"
    role="dialog"
    :style="position"
    @keydown="focusTrap"
  >
    <!-- eslint-enable vuejs-accessibility/no-static-element-interactions -->
    <div class="overflow-auto px-16 py-16">
      <template v-if="type === 'filter'">
        <PriceFilter
          v-if="selectedFacet?.name === 'price' && priceFacet"
          v-model="price"
          :avgPrice="priceFacet.avg"
          clearable
          :currency="defaultCurrency"
          display-infos
          :max="priceFacet.max"
          :prices="priceFacet.values"
          :step="priceFacet.step"
          variant="full"
        />
        <FacetFilter
          v-else-if="isStandardFacet(selectedFacet)"
          v-model="filters[selectedFacet.name]"
          clearable
          :disabled="false"
          display-infos
          :name="selectedFacet.name"
          :title="selectedFacet.title"
          :type="selectedFacet.type"
          :values="selectedFacet.values"
          variant="full"
        />
      </template>
      <Sort v-else v-model:sort="sort" :search-nb-results :sort-options />
    </div>
    <div class="px-16">
      <RevButton class="w-full" variant="primary" @click="handleSeeAll">
        {{ i18n(translations.validate, { count: searchNbResults }) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, useTemplateRef } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { clickOutside as vClickOutside } from '@backmarket/utils/directives/ClickOutside'
import { RevButton } from '@ds/components/Button'

import { useExpandFilters } from '../composables/useExpandFilters'
import { useFilterTracking } from '../composables/useFilterTracking'
import type { Facet, UiPriceFacet } from '../types'

import translations from './MobileFilters.translations'
import FacetFilter from './Refinements/FacetFilter.vue'
import PriceFilter from './Refinements/PriceFilter.vue'
import Sort from './Sort.vue'

const { searchNbResults, priceFacet } = defineProps<{
  searchNbResults: number
  sortOptions: {
    label: string
    value: string
    title: string
  }[]
  facets: Facet[]
  priceFacet?: UiPriceFacet
}>()
const { defaultCurrency } = useMarketplace().market

const i18n = useI18n()
const sort = defineModel<string>('sort', { required: true })
const filters = defineModel<Record<string, string[]>>('filters', {
  required: true,
})
const price = defineModel<[number, number] | undefined>('price', {
  required: true,
})

const modalElement = useTemplateRef<HTMLElement>('modalElement')

const {
  type,
  selectedFacet,
  close,
  focusTrap,
  position,
  modalElement: expandModalElement,
} = useExpandFilters()

const { trackFilterClick } = useFilterTracking()

onMounted(() => {
  expandModalElement.value = modalElement.value
})

function handleClose() {
  close()
}

function handleSeeAll() {
  handleClose()
  trackFilterClick({
    zone: type.value,
    name: 'see_results',
  })
}

function isStandardFacet(
  facet: Facet | UiPriceFacet | undefined,
): facet is Facet {
  return (
    facet !== undefined && facet.name !== undefined && facet.name !== 'price'
  )
}
</script>
